/**
 * Products Page - Admin Portal
 * Enable/disable sellable products per tenant. Capabilities bundled with
 * CRM are shown informationally, never as an independent toggle.
 */
import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { PermissionGate } from '../components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Boxes,
  Search,
  Loader2,
  CheckCircle,
  Building2,
  Zap,
  Settings,
  BarChart3,
  FileText,
  Calendar,
  MessageSquare,
  Bot,
  Sparkles,
  Key,
} from 'lucide-react';
import axios from 'axios';
import {
  correctedLanding, includedWithName, landingOptions as landingOptionsFor, moduleIsHeld,
  sellableProducts as selectSellableProducts, showsAsOwnTile, tenantProductAdoption,
} from './moduleHeld';
import toast from 'react-hot-toast';
import StatGrid from '../../../components/admin/StatGrid';
import MetricCard from '../../../components/admin/MetricCard';
import EmptyState from '../../../components/admin/EmptyState';
import { Skeleton } from '../../../components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

// The product -> landing route map lives in ./moduleHeld
// (`PRODUCT_LANDING_ROUTES`), beside the rules that choose from it, so the
// picker's options can be tested without mounting this page.

// Module icons mapping - matches CRM Setup modules
const moduleIcons = {
  crm: Building2,
  task_manager: CheckCircle,
  schema_builder: Settings,
  import_builder: FileText,
  export_builder: FileText,
  form_builder: FileText,
  flow_builder: Zap,
  survey_builder: MessageSquare,
  chatbot_manager: Bot,
  docflow: FileText,
  file_manager: FileText,
  app_manager: Settings,
  email_templates: FileText,
  booking: Calendar,
  ai_features: Sparkles,
  field_service: Settings,
  reporting: BarChart3,
  features: Sparkles,
  connections: Key,
  sales_console: BarChart3
};

const ModulesPage = () => {
  const { getAdminToken } = useAdminAuth();
  const [availableModules, setAvailableModules] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformStats, setPlatformStats] = useState(null);
  
  // Module management dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantModules, setTenantModules] = useState(null);
  const [saving, setSaving] = useState(false);

  // Landing page config
  const [landingPage, setLandingPage] = useState('');
  const [landingSaving, setLandingSaving] = useState(false);
  // Guards the auto-correction effect below from running against the
  // placeholder initial state before the tenant's real saved value (or
  // confirmation that none exists) has actually loaded.
  const [landingPageLoaded, setLandingPageLoaded] = useState(false);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${ADMIN_API}/modules/available`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setAvailableModules(response.data.modules || []);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    }
  };

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const params = { limit: 100, search: searchQuery || undefined };
      const response = await axios.get(`${ADMIN_API}/tenants`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
        params
      });
      setTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  // Platform-wide adoption analytics for the header strip -- reuses the
  // same GET /admin/dashboard module_usage the Control Plane dashboard
  // already reads, no new endpoint.
  const fetchPlatformStats = async () => {
    try {
      const response = await axios.get(`${ADMIN_API}/dashboard`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setPlatformStats(response.data);
    } catch (error) {
      console.error('Failed to fetch platform stats:', error);
    }
  };

  useEffect(() => {
    fetchModules();
    fetchTenants();
    fetchPlatformStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchTenants();
    }, 300);
    return () => clearTimeout(delaySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the search query changes
  }, [searchQuery]);

  // Sellable products only (deduped: "crm" is a legacy alias of "sales_console",
  // hidden here so "CRM" appears once) -- these are the only independently
  // toggleable items. Bundled capabilities are shown informationally, never
  // as a toggle. Legacy (Field Service) is shown only for tenants that
  // already have it enabled -- never offered fresh.
  const sellableProducts = selectSellableProducts(availableModules);
  const bundledCapabilities = availableModules
    .filter((m) => m.product_category === 'bundled')
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const legacyModules = availableModules.filter((m) => m.product_category === 'legacy');

  const tenantHasCrm = (tenantModules?.enabled_modules || []).some(
    (m) => m === 'sales_console' || m === 'crm'
  );
  // A row is ON when the tenant holds its CANONICAL code or the page's own
  // spelling. Assistants is listed as `chatbot_manager` and a commercially
  // created tenant holds `chatbot`, so comparing spellings alone showed
  // Assistants OFF for a tenant that had it (same rule as Tenant Detail).
  const holds = (m) => moduleIsHeld(m, tenantModules?.enabled_modules);
  // A retired app (Form Builder / Survey Builder) on a tenant that holds
  // Engage is part of Engage, not a Legacy product of its own — closure audit
  // 10-06, M6. Only a holder WITHOUT Engage sees it here, to switch it off.
  const tenantLegacyModules = legacyModules.filter(
    (m) => holds(m) && showsAsOwnTile(m, availableModules, holds));

  const openModuleDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setDialogOpen(true);
    setTenantModules(null);
    setLandingPage('');
    setLandingPageLoaded(false);

    try {
      const response = await axios.get(`${ADMIN_API}/tenants/${tenant.id}/modules`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setTenantModules(response.data);

      // Fetch tenant settings (landing page)
      try {
        const settingsRes = await axios.get(`${ADMIN_API}/tenants/${tenant.id}/settings`, {
          headers: { Authorization: `Bearer ${getAdminToken()}` }
        });
        setLandingPage(settingsRes.data?.default_landing_page || '');
      } catch { /* settings may not exist yet */ }
      finally {
        // Marks the real saved value (or confirmed absence of one) as
        // loaded, whether or not the request succeeded -- unblocks the
        // auto-correction effect below either way.
        setLandingPageLoaded(true);
      }
    } catch (error) {
      console.error('Failed to fetch tenant modules:', error);
    }
  };

  // Persists a landing page value without touching `landingPage` state
  // itself -- used both by the Select's onValueChange (admin-initiated)
  // and by the auto-correction effect (system-initiated), which sets
  // state separately to avoid a save-triggers-effect-triggers-save loop.
  const persistLandingPage = async (newValue, { silent = false } = {}) => {
    setLandingSaving(true);
    try {
      await axios.put(
        `${ADMIN_API}/tenants/${selectedTenant.id}/settings`,
        { default_landing_page: newValue },
        { headers: { Authorization: `Bearer ${getAdminToken()}` } }
      );
      if (!silent) toast.success('Default landing page updated');
    } catch (error) {
      console.error('Failed to save landing page:', error);
      if (!silent) toast.error(error.response?.data?.detail || 'Failed to save landing page');
    } finally {
      setLandingSaving(false);
    }
  };

  const handleLandingPageChange = (newValue) => {
    // The Select only ever offers currently-valid options (see
    // landingOptions below), so this is already structurally impossible
    // to violate -- kept as an explicit guard per the "do not allow saving
    // an invalid landing page" rule, not just an implicit one.
    if (!landingOptions.some((o) => o.path === newValue)) return;
    setLandingPage(newValue);
    persistLandingPage(newValue);
  };

  // Only products enabled for THIS tenant, that are actually sellable
  // (never bundled capabilities, never hidden legacy products), and that
  // have a known CRM route -- CRM Dashboard included only via this same
  // rule, since "sales_console" is just another entry in sellableProducts.
  //
  // A LANDING PAGE IS A PLACE, NOT A PRODUCT. A row another product includes
  // (Form Builder and Survey Builder inside Engage, 27 Sep 2026) is no longer
  // offered as a product to switch, but it is still somewhere a user can
  // land — and dropping it from these options would make the auto-correct
  // below silently REWRITE the saved landing page of every tenant that lands
  // on /form-builder the moment this dialog is opened.
  //
  // ...EXCEPT a retired app on a tenant that holds its replacement (closure
  // audit 10-06, M6): an Engage tenant is offered Engage, never Form Builder
  // or Survey Builder, and a saved /form-builder moves to /engage — where the
  // platform already sent it — rather than to the first option
  // (`correctedLanding`).
  const landingOptions = landingOptionsFor(availableModules, holds);

  // Auto-correct whenever the enabled product set changes (a toggle inside
  // this dialog, or the tenant's real saved value just finished loading and
  // turns out stale): if the current landingPage no longer matches an
  // available option, immediately switch to the first available one (or
  // clear it if there are none) and persist the correction silently. Only
  // acts once landingPageLoaded is true, so this never fires against the
  // placeholder empty state before the tenant's real value has loaded.
  useEffect(() => {
    if (!tenantModules || !landingPageLoaded) return;
    const stillValid = landingOptions.some((o) => o.path === landingPage);
    if (stillValid) return;
    const corrected = correctedLanding(landingPage, landingOptions, availableModules, holds);
    setLandingPage(corrected);
    if (corrected) {
      persistLandingPage(corrected, { silent: true });
      const label = landingOptions.find((o) => o.path === corrected)?.label || corrected;
      toast(`Landing page reset to ${label} (previous page is no longer available)`, { icon: '⚠️' });
    }
    // Intentionally keyed only on the enabled-product set and load state,
    // not landingPage/landingOptions/persistLandingPage, so the correction
    // above doesn't re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantModules, landingPageLoaded]);

  const handleToggleModule = async (moduleApiName, enabled) => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${ADMIN_API}/tenants/${selectedTenant.id}/modules/toggle`,
        { module_api_name: moduleApiName, enabled },
        { headers: { Authorization: `Bearer ${getAdminToken()}` } }
      );
      setTenantModules(response.data);
      toast.success(`${moduleApiName.replace(/_/g, ' ')} ${enabled ? 'enabled' : 'disabled'}`);

      // Update tenant in list
      setTenants(prev => prev.map(t =>
        t.id === selectedTenant.id
          ? { ...t, module_entitlements: response.data.enabled_modules }
          : t
      ));
    } catch (error) {
      console.error('Failed to toggle module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setSaving(false);
    }
  };

  const totalTenantsForAdoption = platformStats?.total_tenants || 0;
  const productsWithAdoption = sellableProducts.filter(
    (m) => (platformStats?.module_usage?.[m.api_name] || 0) > 0
  ).length;
  const avgSeatUtilization = tenants.length > 0
    ? Math.round(
        tenants.reduce((sum, t) => sum + Math.min(100, ((t.current_users || 0) / (t.seat_limit || 10)) * 100), 0) / tenants.length
      )
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Products</h1>
        <p className="text-muted-foreground mt-1">
          Enable or disable products for each tenant
        </p>
      </div>

      {/* Analytics */}
      {!platformStats ? (
        <StatGrid cols={4}>
          {Array.from({ length: 4 }).map((_, i) => <MetricCard key={i} loading />)}
        </StatGrid>
      ) : (
        <StatGrid cols={4}>
          <MetricCard label="Sellable Products" value={sellableProducts.length} icon={Boxes} accent="primary" />
          <MetricCard
            label="Adopted Products"
            value={productsWithAdoption}
            icon={CheckCircle}
            accent="success"
            hint={`${productsWithAdoption} of ${sellableProducts.length} products have at least one tenant using them.`}
          />
          <MetricCard
            label="Tenants (global)"
            value={totalTenantsForAdoption}
            icon={Building2}
            accent="sky"
            hint="All tenants in the platform aggregate; the cards below show only the current page."
          />
          <MetricCard
            label="Avg. Seat Utilization"
            value={avgSeatUtilization}
            format={(v) => `${Math.round(v)}%`}
            icon={Zap}
            accent="violet"
            hint="Mean of (current users / seat limit) across tenants currently loaded on this page."
          />
        </StatGrid>
      )}

      {/* Sellable Products Overview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sellable Products</CardTitle>
          <CardDescription>
            Products customers can purchase individually or as part of an agreement.
            Capabilities bundled with CRM (Schema Builder, App Manager, etc.)
            are enabled automatically and are not listed separately.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {sellableProducts.map((module) => {
              const Icon = moduleIcons[module.api_name] || Boxes;
              const adoption = platformStats?.module_usage?.[module.api_name] || 0;
              return (
                <div
                  key={module.api_name}
                  className="p-3 border rounded-lg bg-muted/40 card-hover-lift"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm text-foreground">{module.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {module.is_premium && (
                      <Badge className="text-xs bg-warning/10 text-warning-text border-warning/20">Premium</Badge>
                    )}
                    {platformStats && (
                      <span className="text-xs text-muted-foreground">{adoption} tenants</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tenant Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tenants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="tenant-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tenants Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : tenants.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <EmptyState icon={Building2} title="No tenants found" />
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-xs text-muted-foreground" data-testid="tenant-page-count">
            Showing {tenants.length} tenant{tenants.length === 1 ? '' : 's'} on this page
            {totalTenantsForAdoption ? ` of ${totalTenantsForAdoption} globally` : ''}.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map((tenant) => {
            const adoption = tenantProductAdoption(
              tenant.module_entitlements, availableModules);
            return (
            <Card 
              key={tenant.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openModuleDialog(tenant)}
              data-testid={`tenant-module-card-${tenant.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {tenant.tenant_name?.[0] || 'T'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{tenant.tenant_name}</p>
                      <Badge variant="outline" className="text-xs">
                        {tenant.commercial_state?.label || 'Commercial state unavailable'}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Settings className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-1">
                  {adoption.products.map((m) => {
                    const code = m.canonical_code || m.api_name;
                    const Icon = moduleIcons[m.api_name] || moduleIcons[code] || Boxes;
                    return (
                      <Badge key={code} variant="outline" className="text-xs flex items-center gap-1">
                        <Icon className="h-3 w-3" />
                        {m.name}
                      </Badge>
                    );
                  })}
                  {adoption.runtimeEntitlementCount === 0 && (
                    <span className="text-xs text-slate-400">No runtime entitlements recorded</span>
                  )}
                </div>
                {adoption.runtimeEntitlementCount > 0 && (
                  <p className="mt-2 text-[11px] text-muted-foreground"
                     data-testid={`tenant-runtime-entitlement-count-${tenant.id}`}>
                    {adoption.runtimeEntitlementCount} runtime entitlement
                    {adoption.runtimeEntitlementCount === 1 ? '' : 's'} · {adoption.products.length} customer product
                    {adoption.products.length === 1 ? '' : 's'}
                  </p>
                )}
              </CardContent>
            </Card>
          );})}
          </div>
        </>
      )}

      {/* Module Management Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Products</DialogTitle>
            <DialogDescription>
              Manage products for {selectedTenant?.tenant_name}
            </DialogDescription>
          </DialogHeader>

          {!tenantModules ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Tenant Info */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">
                    {selectedTenant?.tenant_name?.[0] || 'T'}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">{selectedTenant?.tenant_name}</p>
                  <p className="text-sm text-slate-500">
                    Commercial state:{' '}
                    <span className="font-medium">
                      {selectedTenant?.commercial_state?.label || 'Unavailable'}
                    </span>
                  </p>
                </div>
              </div>

              {/* Default Landing Page Config — options are generated from this
                  tenant's currently enabled products only (never disabled
                  products, bundled capabilities, or hidden legacy products).
                  CRM Dashboard appears only when CRM Console is enabled. */}
              <div className="p-4 border border-indigo-200 rounded-lg bg-indigo-50/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Default Landing Page</p>
                    <p className="text-xs text-slate-500 mt-0.5">Where users land after login</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {landingOptions.length === 0 ? (
                      <p className="text-xs text-amber-600 w-52 text-right">
                        No available landing page. Enable at least one product.
                      </p>
                    ) : (
                      <Select value={landingPage} onValueChange={handleLandingPageChange}>
                        <SelectTrigger className="w-52 h-9" data-testid="landing-page-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {landingOptions.map((option) => (
                            <SelectItem key={option.path} value={option.path}>
                              {option.label === 'CRM' ? 'CRM Dashboard' : option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {landingSaving && <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />}
                  </div>
                </div>
              </div>

              {/* Products — the only independently toggleable items */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Products</h4>
                <div className="space-y-2">
                  {sellableProducts.map((module) => {
                    const Icon = moduleIcons[module.api_name] || Boxes;
                    const isEnabled = holds(module);
                    const includedWith = includedWithName(module, availableModules, holds);

                    return (
                      <div
                        key={module.api_name}
                        className={`flex items-center justify-between p-3 border rounded-lg ${
                          isEnabled ? 'border-indigo-200 bg-indigo-50' : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`h-5 w-5 ${isEnabled ? 'text-indigo-600' : 'text-slate-400'}`} />
                          <div>
                            <p className="font-medium text-sm">{module.name}</p>
                            <p className="text-xs text-slate-500">{module.description}</p>
                          </div>
                          {module.is_premium && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">Premium</Badge>
                          )}
                        </div>
                        {/* Included with another product: no switch of its own
                            (same rule as Tenant Detail, `includedWithName`). */}
                        {includedWith ? (
                          <span className="text-xs text-slate-500 text-right"
                                data-testid={`included-with-${module.api_name}`}>
                            Included with {includedWith}
                          </span>
                        ) : (
                          <PermissionGate permission="module:write">
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={(checked) => handleToggleModule(module.api_name, checked)}
                              disabled={saving}
                            />
                          </PermissionGate>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Included with CRM — informational only, never a separate toggle */}
              {tenantHasCrm && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-1">Included with CRM</h4>
                  <p className="text-xs text-slate-500 mb-3">
                    Automatically available because CRM is enabled. These are not sold or toggled separately.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {bundledCapabilities.map((module) => {
                      const Icon = moduleIcons[module.api_name] || Boxes;
                      return (
                        <div
                          key={module.api_name}
                          className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-600"
                        >
                          <Icon className="h-4 w-4" />
                          <span className="text-sm">{module.name}</span>
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-500 ml-auto" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Legacy — shown only for tenants that already have it; never offered fresh */}
              {tenantLegacyModules.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-1">Legacy</h4>
                  <p className="text-xs text-slate-500 mb-3">
                    No longer offered in new agreements. This tenant already has access and continues to work unchanged.
                  </p>
                  <div className="space-y-2">
                    {tenantLegacyModules.map((module) => {
                      const Icon = moduleIcons[module.api_name] || Boxes;
                      const isEnabled = holds(module);
                      // Form Builder / Survey Builder on an Engage tenant are
                      // part of Engage, not a legacy product to switch.
                      const includedWith = includedWithName(module, availableModules, holds);
                      return (
                        <div
                          key={module.api_name}
                          className="flex items-center justify-between p-3 border border-amber-200 bg-amber-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="h-5 w-5 text-amber-600" />
                            <div>
                              <p className="font-medium text-sm">{module.name}</p>
                              <p className="text-xs text-slate-500">{module.description}</p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-700 text-xs">Legacy</Badge>
                          </div>
                          {includedWith ? (
                            <span className="text-xs text-slate-500 text-right"
                                  data-testid={`included-with-${module.api_name}`}>
                              Included with {includedWith}
                            </span>
                          ) : (
                            <PermissionGate permission="module:write">
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={(checked) => handleToggleModule(module.api_name, checked)}
                                disabled={saving}
                              />
                            </PermissionGate>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModulesPage;
